// react
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";

// prop-types
import PropTypes from "prop-types";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TableFooter,
  TablePagination,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  TableSortLabel,
  Tooltip,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import {
  KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon,
  KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon,
  Search as SearchIcon,
  ManageSearch as ManageSearchIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";

// ui-components
import SearchDialog from "./SearchDialog";

// record-controller
import fetchPaginatedRecords from "record-controller/fetchPaginatedRecords";
import fetchSignature from "record-controller/fetchSignature";

// firebase
import { db } from "config/firebase";
import { onSnapshot, query, collection, orderBy as orderByQuery, where } from "firebase/firestore";

// utils
import convertTimestamp from "utils/convertTimestamp";

// A ---------------------------------------------------------------------- M

const CDNFileTableToolbar = ({ isTagGroupMember, handleOpenSearchDialog }) => {
  const { t } = useTranslation();

  return (
    <Grid container alignItems="center" spacing={2} mb={2}>
      <Grid item xs={12} sm={7}>
        <Typography variant="h6" fontWeight={700}>
          {t("documents")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={5} container justifyContent="flex-end" spacing={1}>
        {isTagGroupMember && (
          <Grid item xs={12} sm={4}>
            <Button
              onClick={handleOpenSearchDialog}
              startIcon={<SearchIcon />}
              variant="contained"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
              }}
              fullWidth
            >
              {t("search")}
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const CDNFileTableHead = ({ order, orderBy, onRequestSort }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const headCells = useMemo(
    () => [
      { id: "name", label: t("name") },
      { id: "timestamp", label: t("upload_datetime") },
      { id: "document_type", label: t("document_type") },
      { id: "actions", label: t("actions") },
    ],
    [t]
  );

  const createSortHandler = (property) => () => {
    if (property === "timestamp") {
      onRequestSort(property);
    }
  };

  return (
    <TableHead sx={{ background: `linear-gradient(90deg, ${theme.palette.primary.main}33, ${theme.palette.primary.dark}11)` }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align={headCell.id !== "actions" ? "left" : "right"} sx={{ fontWeight: 700 }}>
            {headCell.id === "timestamp" ? (
              <TableSortLabel active={orderBy === headCell.id} direction={orderBy === headCell.id ? order : "asc"} onClick={createSortHandler(headCell.id)}>
                {headCell.label}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

CDNFileTableHead.propTypes = {
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  onRequestSort: PropTypes.func.isRequired,
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        {isRtl ? <KeyboardArrowRightOutlinedIcon /> : <KeyboardArrowLeftOutlinedIcon />}
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {isRtl ? <KeyboardArrowLeftOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const ArchivedDocsTable = ({ tag, isTagGroupMember, onOpenDetails, onOpenPreservationProof }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const searchKeyLabels = {
    startDate: "start_upload",
    endDate: "end_upload",
    documentNames: "metadata.documentnames",
    soggettiFullName: "metadata.subjectfullname",
    soggettiCodiceFiscale: "metadata.subjecttaxcode",
    soggettiOrganizzazione: "metadata.subjectorganization",
  };

  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("timestamp");
  const [order, setOrder] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState(null);

  const recordCacheRef = useRef({});
  const lastDocCacheRef = useRef({});
  const pageRef = useRef(page);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const resetState = () => {
    setRecords([]);
    recordCacheRef.current = {};
    lastDocCacheRef.current = {};
  };

  const fetchData = useCallback(async () => {
    if (recordCacheRef.current[pageRef.current]) {
      setRecords(recordCacheRef.current[pageRef.current]);
      return;
    }

    setIsLoading(true);

    try {
      const lastDoc = pageRef.current > 0 ? lastDocCacheRef.current[pageRef.current - 1] : null;
      const { records: newRecords, lastDoc: newLastDoc } = await fetchPaginatedRecords(tag.id, "CDNFile", isTagGroupMember, lastDoc, rowsPerPage, orderBy, order, searchCriteria);
      recordCacheRef.current[pageRef.current] = newRecords;
      lastDocCacheRef.current[pageRef.current] = newLastDoc;
      setRecords(newRecords);
    } catch (error) {
      console.error("Error fetching paginated records:", error);
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  }, [tag.id, isTagGroupMember, page, rowsPerPage, orderBy, order, searchCriteria]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!tag.id) return;

    const collectionRef = collection(db, "tagsdata", tag.id, "signatures");
    const q = query(collectionRef, where("type", "==", "CDNFile"), where("timestamp", ">=", Date.now().toString()), orderByQuery("timestamp", "desc"));
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      try {
        const recordPromises = snapshot.docs.map((doc) => fetchSignature(doc.id));
        const detailedRecords = await Promise.all(recordPromises);
        const newRecords = detailedRecords.filter((record) => record !== null);

        if (newRecords.length === 0) return;

        if (pageRef.current === 0) {
          recordCacheRef.current = {};
          lastDocCacheRef.current = {};
          fetchData();
        } else {
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Error processing new records:", error);
      }
    });

    return () => unsubscribe();
  }, [tag.id, isTagGroupMember, rowsPerPage, orderBy, order]);

  const handleChangePage = (event, newPage) => {
    pageRef.current = newPage;
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    pageRef.current = 0;
    setPage(0);
    resetState();
  };

  const handleRequestSort = (property) => {
    if (property === "timestamp") {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
      pageRef.current = 0;
      setPage(0);
      resetState();
    }
  };

  const handleNewDataAvailable = () => {
    recordCacheRef.current = {};
    lastDocCacheRef.current = {};

    if (pageRef.current === 0) {
      fetchData();
    } else {
      pageRef.current = 0;
      setPage(0);
    }

    setSnackbarOpen(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleOpenSearchDialog = () => setIsSearchDialogOpen(true);
  const handleCloseSearchDialog = () => setIsSearchDialogOpen(false);

  const handleSearch = (criteria) => {
    setSearchCriteria(criteria);
    pageRef.current = 0;
    setPage(0);
    resetState();
  };

  if (isLoading) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <>
      <Box>
        <CDNFileTableToolbar isTagGroupMember={isTagGroupMember} handleOpenSearchDialog={handleOpenSearchDialog} />

        {searchCriteria && (
          <Box mb={2}>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {Object.entries(searchCriteria).map(([key, value]) =>
                value ? (
                  <Chip
                    key={key}
                    label={`${t(searchKeyLabels[key] || key)}: ${["startDate", "endDate"].includes(key) ? convertTimestamp(value) : value}`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 500, fontSize: "0.9rem" }}
                  />
                ) : null
              )}
              <Chip
                label={t("clear_search")}
                onDelete={() => {
                  setSearchCriteria(null);
                  resetState();
                  pageRef.current = 0;
                  setPage(0);
                }}
                color="error"
                variant="filled"
                sx={{ fontWeight: "bold", fontSize: "0.9rem" }}
              />
            </Box>
          </Box>
        )}

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}CC)`,
            backdropFilter: "blur(6px)",
            border: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
          }}
        >
          <Table>
            {!isMobile && <CDNFileTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />}

            <TableBody>
              {records.map((record) => {
                const documentName = record.metadata?.documenti?.find((doc) => doc.idDocumento === record.metadata?.documentoPrincipale)?.nomeDocumento || <Typography color="gray">N/A</Typography>;
                const timestamp = record.timestamp ? convertTimestamp(record.timestamp) : t("no_data");
                const docType = record.metadata?.tipologiaDocumentale || t("no_data");

                return isMobile ? (
                  <Box
                    key={record.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      mx: 1,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                      cursor: "pointer",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.01)",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                      },
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} sx={{ maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {documentName}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {timestamp}
                    </Typography>

                    <Chip
                      label={docType}
                      size="small"
                      sx={{
                        mt: 1,
                        borderRadius: 2,
                        fontWeight: 600,
                        backgroundColor: `${theme.palette.primary.main}15`,
                        color: theme.palette.primary.main,
                      }}
                    />

                    <Box mt={2} display="flex" gap={1}>
                      <Button fullWidth variant="outlined" startIcon={<ManageSearchIcon fontSize="small" />} sx={{ textTransform: "none", borderRadius: 3 }} onClick={() => onOpenDetails(record)}>
                        {t("details")}
                      </Button>

                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<VerifiedIcon fontSize="small" />}
                        color="success"
                        sx={{ textTransform: "none", borderRadius: 3 }}
                        onClick={() => onOpenPreservationProof(record)}
                      >
                        {t("preservation_proof")}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <TableRow
                    key={record.id}
                    sx={{
                      cursor: "pointer",
                      transition: "background 0.2s ease",
                      "&:hover": {
                        backgroundColor: `${theme.palette.primary.main}15`,
                        transform: "scale(1.002)",
                        transition: "all 0.2s ease",
                      },
                    }}
                  >
                    <TableCell sx={{ maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 600 }}>{documentName}</TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {timestamp}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={docType}
                        size="small"
                        sx={{
                          borderRadius: 2,
                          fontWeight: 600,
                          backgroundColor: `${theme.palette.primary.main}15`,
                          color: theme.palette.primary.main,
                        }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Tooltip title={t("details")} placement="top">
                        <IconButton onClick={() => onOpenDetails(record)}>
                          <ManageSearchIcon color="primary" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={t("preservation_proof")} placement="top">
                        <IconButton onClick={() => onOpenPreservationProof(record)}>
                          <VerifiedIcon color="success" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>

            {!isMobile && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 20]}
                    count={records.length < rowsPerPage ? page * rowsPerPage + records.length : (page + 1) * rowsPerPage + 1}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t("rows_per_page")}
                    labelDisplayedRows={({ from, to, count }) => {
                      const displayCount = records.length === rowsPerPage ? `${count}+` : count;
                      return t("displayed_rows", { from, to, count: displayCount });
                    }}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>

          {isMobile && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <TablePaginationActions
                count={records.length < rowsPerPage ? page * rowsPerPage + records.length : (page + 1) * rowsPerPage + 1}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
              />
            </Box>
          )}
        </TableContainer>

        <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert
            onClose={handleSnackbarClose}
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={handleNewDataAvailable}>
                {t("reload")}
              </Button>
            }
          >
            {t("new_data_available")}
          </Alert>
        </Snackbar>
      </Box>

      <SearchDialog open={isSearchDialogOpen} onClose={handleCloseSearchDialog} onSearch={handleSearch} />
    </>
  );
};

export default ArchivedDocsTable;
